import axios from "axios";
import { UserKeys } from "src/constants/userDetails";
import { API_URL, GLOBAL_SERVICE_URL } from "src/scenes/App";

export type MeetingStatus = "scheduled" | "starting" | "active" | "ended" | "cancelled" | "expired";
export type AdmissionStatus = "invited" | "waiting" | "admitted" | "declined" | "left" | "removed";

export interface PublicMeeting {
    meetingToken: string;
    title: string;
    hostName: string;
    businessName: string | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    timezone: string;
    status: MeetingStatus;
    passwordRequired: boolean;
    connectedParticipantCount: number;
    guestApprovalRequired: true;
}

export interface MeetingParticipant {
    participantUuid: string;
    employeeUuid: string | null;
    email: string | null;
    displayName: string;
    participantType: "host" | "internal" | "guest";
    invitationStatus: string;
    admissionStatus: AdmissionStatus;
    matrixUserId: string | null;
    joinedAt: string | null;
    leftAt: string | null;
}

export interface MeetingDetail {
    canManage: boolean;
    meetingUuid: string;
    meetingToken: string;
    meetingUrl: string;
    title: string;
    description: string | null;
    hostEmployeeUuid: string;
    hostName: string;
    calendarItemUuid: string | null;
    chatUuid: string | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    timezone: string;
    status: MeetingStatus;
    passwordEnabled: boolean;
    guestApprovalRequired: true;
    matrixRoomId: string | null;
    maxParticipants: number;
    participants: MeetingParticipant[];
    occurrence: {
        occurrenceUuid: string;
        recurrenceId: string;
        scheduledStart: string | null;
        scheduledEnd: string | null;
        status: MeetingStatus;
        matrixRoomId: string | null;
        startedAt: string | null;
        endedAt: string | null;
    };
}

export interface GuestMeetingSession {
    guestSessionUuid: string;
    sessionToken?: string;
    status: AdmissionStatus;
    expiresAt: string;
    matrix?: {
        userId: string;
        deviceId: string;
        accessToken: string;
        roomId: string;
    };
}

const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem(UserKeys.TOKEN) || ""}` },
});

type RegionalApi = {
    apiUrl?: string;
};

let resolvedPublicMeetingApiUrl: string | null = null;

const trimTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

const getLocalMeetingApiUrl = (): string | null => {
    if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        return null;
    }
    return `http://${window.location.hostname}:8880`;
};

export const getMeetingApiBaseUrl = (): string => (
    API_URL
    || resolvedPublicMeetingApiUrl
    || getLocalMeetingApiUrl()
    || window.location.origin
);

const fetchPublicMeeting = async (apiUrl: string, meetingToken: string): Promise<PublicMeeting> => (
    await axios.get<PublicMeeting>(`${trimTrailingSlash(apiUrl)}/meet/${encodeURIComponent(meetingToken)}`)
).data;

const discoverPublicMeeting = async (meetingToken: string): Promise<PublicMeeting> => {
    const configuredApiUrl = API_URL || getLocalMeetingApiUrl();
    if (configuredApiUrl) {
        resolvedPublicMeetingApiUrl = trimTrailingSlash(configuredApiUrl);
        return fetchPublicMeeting(resolvedPublicMeetingApiUrl, meetingToken);
    }

    const regionResponse = await axios.get<RegionalApi[]>(`${GLOBAL_SERVICE_URL}/regions`);
    let lastError: unknown = null;
    for (const region of regionResponse.data) {
        if (!region.apiUrl) {
            continue;
        }
        const regionalApiUrl = trimTrailingSlash(region.apiUrl);
        try {
            const meeting = await fetchPublicMeeting(regionalApiUrl, meetingToken);
            resolvedPublicMeetingApiUrl = regionalApiUrl;
            return meeting;
        } catch (error) {
            lastError = error;
        }
    }

    if (lastError) {
        throw lastError;
    }
    throw new Error("No regional API is available for this meeting.");
};

export const getPublicMeeting = async (meetingToken: string): Promise<PublicMeeting> => (
    discoverPublicMeeting(meetingToken)
);

export const requestGuestMeetingSession = async (
    meetingToken: string,
    displayName: string,
    password: string,
    browserIdentity: string
): Promise<GuestMeetingSession> => (
    await axios.post<GuestMeetingSession>(`${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions`, {
        displayName,
        password,
        browserIdentity,
    })
).data;

export const getGuestMeetingSession = async (
    meetingToken: string,
    sessionToken: string
): Promise<GuestMeetingSession> => (
    await axios.get<GuestMeetingSession>(`${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions/current`, {
        headers: { "X-Meeting-Session": sessionToken },
    })
).data;

export const leaveGuestMeeting = async (meetingToken: string, sessionToken: string): Promise<void> => {
    await axios.post(
        `${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions/current/leave`,
        {},
        { headers: { "X-Meeting-Session": sessionToken } }
    );
};

export const createInstantMeeting = async (chatUuid: string, callId: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/instant/${encodeURIComponent(chatUuid)}`,
        { callId },
        getAuthConfig()
    )
).data;

export const getMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.get<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}`, getAuthConfig())
).data;

export const getAuthenticatedMeetingByToken = async (meetingToken: string): Promise<MeetingDetail> => (
    await axios.get<MeetingDetail>(
        `${API_URL}/meetings/by-token/${encodeURIComponent(meetingToken)}`,
        getAuthConfig()
    )
).data;

export const startMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/start`, {}, getAuthConfig())
).data;

export const joinInternalMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/join`, {}, getAuthConfig())
).data;

export const joinInternalMeetingByToken = async (meetingToken: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/by-token/${encodeURIComponent(meetingToken)}/join`,
        {},
        getAuthConfig()
    )
).data;

export const leaveInternalMeeting = async (meetingUuid: string): Promise<MeetingParticipant> => (
    await axios.post<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/leave`,
        {},
        getAuthConfig()
    )
).data;

export const inviteMeetingGuests = async (
    meetingUuid: string,
    guestEmails: string[],
    password: string
): Promise<MeetingParticipant[]> => (
    await axios.post<MeetingParticipant[]>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/guest-invitations`,
        { guestEmails, password },
        getAuthConfig()
    )
).data;

export const updateMeetingAdmission = async (
    meetingUuid: string,
    participantUuid: string,
    status: "admitted" | "declined"
): Promise<MeetingParticipant> => (
    await axios.put<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/participants/${encodeURIComponent(participantUuid)}/admission`,
        { status },
        getAuthConfig()
    )
).data;

export const endMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/end`, {}, getAuthConfig())
).data;

export const removeMeetingParticipant = async (
    meetingUuid: string,
    participantUuid: string
): Promise<MeetingParticipant> => (
    await axios.delete<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/participants/${encodeURIComponent(participantUuid)}`,
        getAuthConfig()
    )
).data;
